/**
 * The update flow, in one place, used by both the banner and the Settings row.
 *
 * The bug this replaces: permission was checked once on mount and cached. If
 * Android hadn't yet been told Lift may install packages, the UI showed a
 * "grant permission" button; tapping it opened Settings; granting it and
 * returning left the CACHED "no permission" state untouched — so the button
 * kept bouncing you back to Settings forever, with no way to actually update.
 *
 * Two changes fix it:
 *
 *  1. Permission is re-checked every time the app returns to the foreground,
 *     so coming back from Settings updates the state.
 *  2. Intent is remembered. If you tapped Update and were sent to Settings, the
 *     download starts BY ITSELF when you come back having granted it. Making
 *     the user find and press the same button again — after they already told
 *     us what they wanted — is the kind of small insult that makes software
 *     feel hostile.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { withTimeout } from '../lib/timeout'
import { isNative } from './platform'
import {
  canInstallNow,
  checkForUpdate,
  installUpdate,
  openInstallSettings,
  type UpdateStatus,
} from './update'

export type UpdatePhase =
  | 'idle'
  | 'checking'
  | 'available'
  | 'awaiting-permission'
  | 'downloading'
  /** Bytes are on disk and Android's installer is up. We are waiting on a human. */
  | 'installing'
  | 'error'
  | 'up-to-date'

const SNOOZE_KEY = 'lift.update.snooze'
/** "Later" means later today, not never. */
const SNOOZE_MS = 8 * 60 * 60 * 1000

/**
 * How stale a check has to be before returning to the foreground re-runs it.
 * Android keeps the activity alive, so mount happens once and then possibly
 * never again for days — without a re-check the banner can only ever appear
 * after a cold start. Short enough that "publish, then open the app" works;
 * long enough that flicking between apps isn't a request per switch.
 */
const RECHECK_MS = 5 * 60 * 1000

type Snooze = { versionCode: number; until: number }

/**
 * Snoozing the banner.
 *
 * This used to be permanent per version: one tap on "Later" and the banner
 * never returned for that build, leaving a Settings row as the only way to
 * update. An accidental tap silently cost you the whole mechanism. A dismissal
 * should mean "not now", not "never" — so it expires, and Settings can always
 * bring it back.
 */
function readSnooze(): Snooze | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Snooze
    if (typeof parsed?.versionCode !== 'number' || typeof parsed?.until !== 'number') {
      return null   // legacy plain-number value from the permanent scheme
    }
    return parsed
  } catch {
    return null
  }
}

export function useUpdate() {
  const [status, setStatus] = useState<UpdateStatus | null>(null)
  const [phase, setPhase] = useState<UpdatePhase>('idle')
  const [percent, setPercent] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [checkedAt, setCheckedAt] = useState<Date | null>(null)
  const [snooze, setSnoozeState] = useState<Snooze | null>(() => readSnooze())

  // Set when the user asked to update but had to be sent to Settings first.
  const wantsInstall = useRef(false)
  // When the server was last asked. A ref, not the checkedAt state, so the
  // foreground handler can read it without re-subscribing on every check.
  const lastCheckedAt = useRef(0)

  const check = useCallback(async () => {
    setPhase('checking')
    setError(null)
    lastCheckedAt.current = Date.now()
    try {
      const s = await withTimeout(checkForUpdate(), 15000, 'Update check')
      setStatus(s)
      setCheckedAt(new Date())
      setPhase(s.available ? 'available' : 'up-to-date')
      return s
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check failed.')
      setPhase('error')
      return null
    }
  }, [])

  const startDownload = useCallback(async (s: UpdateStatus) => {
    if (!s.latest) return
    wantsInstall.current = false
    setPhase('downloading')
    setPercent(null)
    setError(null)
    try {
      const outcome = await installUpdate(s.latest, setPercent)

      // The grant vanished between the check and the launch. Don't strand the
      // user on a screen that will never move — send them to the toggle and
      // remember that they still want this.
      if (outcome.needsPermission) {
        wantsInstall.current = true
        setPhase('awaiting-permission')
        const opened = await openInstallSettings()
        if (!opened) {
          setError(
            'Android blocked the install and the settings screen would not '
            + 'open. In Settings → Apps → Lift, allow it to install unknown '
            + 'apps, then tap Update again.',
          )
        }
        return
      }

      // Android's installer dialog is now up, and the rest is out of our hands.
      // This is deliberately NOT still 'downloading': that phase used to persist
      // after the hand-off, and the foreground re-check meant to clear it
      // skipped itself while it was set — so a declined install left the banner
      // reading "Downloading…" forever, with the Settings row as the only way
      // through. A phase that means "waiting on you" can be recovered from.
      setPhase('installing')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.')
      setPhase('error')
    }
  }, [])

  /** The single button action: update now, or get permission and then update. */
  const update = useCallback(async () => {
    const s = status
    if (!s?.latest) return

    const allowed = await canInstallNow()
    if (allowed) {
      await startDownload(s)
      return
    }

    // Remember the intent, then send them to Settings. The foreground handler
    // below picks it up when they come back.
    wantsInstall.current = true
    setPhase('awaiting-permission')
    const opened = await openInstallSettings()
    if (!opened) {
      setError(
        'Couldn\'t open the permission screen. In Settings → Apps → Lift, '
        + 'allow it to install unknown apps, then tap Update again.',
      )
    }
  }, [status, startDownload])

  // Re-check on every return to the foreground. This is what makes coming back
  // from Settings work at all — without it the permission state is whatever it
  // was when the component mounted, forever.
  useEffect(() => {
    if (!isNative()) return

    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return

      const allowed = await canInstallNow()
      setStatus((prev) => (prev ? { ...prev, canInstall: allowed } : prev))

      if (wantsInstall.current && allowed && status?.latest) {
        // They asked to update, went to Settings, and granted it. Carry on
        // without making them ask twice.
        await startDownload(status)
        return
      }
      if (wantsInstall.current && !allowed) {
        setPhase('awaiting-permission')
        setError(
          'Permission still looks off. In Settings, allow Lift to install '
          + 'unknown apps, then tap Update again.',
        )
        return
      }

      // Coming back from Android's installer. Either it worked — in which case
      // the app has been replaced and this code is gone — or it didn't, and the
      // honest thing is to offer the update again rather than keep pretending
      // something is in flight.
      if (phase === 'installing') {
        setPercent(null)
        setPhase('available')
        setError(
          allowed
            ? 'That install didn\'t finish. Tap Update to try again.'
            : 'Android blocked the install. Allow Lift to install unknown apps, '
              + 'then tap Update again.',
        )
        return
      }

      // Ask the server again if the last answer is stale. Without this the
      // check only ever runs at mount, and since Android resumes the existing
      // activity rather than restarting it, a version published while the app
      // sat in the background is never noticed — the banner looks broken when
      // it simply was never told there was anything to show.
      //
      // 'downloading' is excluded because bytes really are in flight. Nothing
      // else may be, or the state machine can get stuck in it.
      if (phase === 'checking' || phase === 'downloading') return
      if (Date.now() - lastCheckedAt.current < RECHECK_MS) return
      await check()
    }

    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [status, startDownload, phase, check])

  useEffect(() => { void check() }, [check])

  const snoozeBanner = useCallback((versionCode: number) => {
    const next = { versionCode, until: Date.now() + SNOOZE_MS }
    try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(next)) } catch { /* off */ }
    setSnoozeState(next)
  }, [])

  const unsnooze = useCallback(() => {
    try { localStorage.removeItem(SNOOZE_KEY) } catch { /* off */ }
    setSnoozeState(null)
  }, [])

  const isSnoozed =
    snooze !== null
    && status?.latest?.versionCode === snooze.versionCode
    && Date.now() < snooze.until

  return {
    status,
    phase,
    percent,
    error,
    checkedAt,
    check,
    update,
    openSettings: openInstallSettings,
    isNative: isNative(),
    isSnoozed,
    snoozedUntil: snooze?.until ?? null,
    snoozeBanner,
    unsnooze,
  }
}
